import { eq } from "drizzle-orm";
import * as amplitude from "@amplitude/analytics-browser";
import * as Sentry from "@sentry/browser";
import { useLDClient } from "launchdarkly-react-client-sdk";
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import type { WorkspaceMembership } from "@storyboard/web-client";
import type { Device, UserInfoResponse } from "@storyboard/web-client/auth";
import ElectronComponentLoader from "@/components/electron/ElectronComponentLoader.tsx";
import ElectronNotifications from "@/components/electron/ElectronNotifications.tsx";
import InvalidWorkspaceModal from "@/components/InvalidWorkspaceModal";
import LoadingScreen from "@/components/LoadingScreen.tsx";
import NoValidWorkspacesModal from "@/components/NoValidWorkspacesModal.tsx";
import { config } from "@/config.ts";
import { getWorkspaceConfig } from "@/data/workspaceConfig.ts";
import { db } from "@/db/db";
import { account } from "@/db/schema.ts";
import { identifyLaunchDarklyUser } from "@/init/identifyLaunchDarklyUser";
import { createLogger } from "@/logger";
import { bulkUpsertAccount, upsertMyAccount } from "@/models/repositories/Account";
import { bulkUpsertWorkspace } from "@/models/repositories/Workspace";
import { bulkUpsertWorkspaceMembership } from "@/models/repositories/WorkspaceMembership";
import { AuthContext } from "@/models/StateProviders/AuthProvider.tsx";
import { useTelemetry } from "@/models/TelemetryProvider.tsx";
import { TrackingContext } from "@/models/TrackingStateProvider.tsx";
import { ResetCoordinator } from "@/services/ResetCoordinator";
import { ipcRendererCallback, ipcRendererSend, isElectron, removeIpcListener } from "@/utils";

const log = createLogger("feed");

type UserState = {
  user?: UserInfoResponse | null;
  workspaceId?: string | null;
  fetchUserDevice?: (token: string) => Device | null;
  workspaceMembership?: WorkspaceMembership | null;
  tokens?: string[];
  appVersion?: string;
  dispatcherFunction: string;
  setDispatcherFunction?: (value: string) => void;
};
const LANGUAGE_SESSION_KEY = "preferredLanguage";
const defaultAppVersion = "0.0.0";
export const UserContext = createContext<UserState>({
  user: null,
  workspaceId: null,
  fetchUserDevice: undefined,
  workspaceMembership: null,
  tokens: [],
  appVersion: defaultAppVersion,
  dispatcherFunction: "",
});
const UserProvider = ({ children }: { children: React.ReactNode }) => {
  /**
   *  User is retrieved from AuthContext
   */

  const { setAttribute } = useTelemetry();
  const { user } = useContext(AuthContext);
  const { workspaceId } = useParams();
  const workspaceConfig = getWorkspaceConfig(workspaceId);
  const ldClient = useLDClient();
  const { ampli } = useContext(TrackingContext);
  const sessionStorageValue = window?.sessionStorage?.getItem(LANGUAGE_SESSION_KEY);
  const [currentUserWorkspaceId, setCurrentUserWorkspaceId] = useState<string | null>(workspaceId);
  const [appVersion, setAppVersion] = useState<string>(defaultAppVersion);
  const workspaceMembership = user?.workspaceMemberships?.find(
    (membership) => membership.workspaceId === currentUserWorkspaceId,
  );
  const [dispatcherFunction, setDispatcherFunction] = useState<string>(
    workspaceMembership?.config?.dispatcherFunction ?? "",
  );

  useEffect(() => {
    if (workspaceId && user?.workspaces?.length > 0 && user?.workspaces?.some((ws) => ws.id === workspaceId)) {
      setCurrentUserWorkspaceId(workspaceId);
    }
  }, [workspaceId, user]);

  useEffect(() => {
    const preferred = workspaceMembership?.config?.dispatcherFunction ?? workspaceConfig?.dispatcherFunction;
    if (preferred) {
      setDispatcherFunction(preferred);
    }
  }, [workspaceMembership?.config?.dispatcherFunction, workspaceConfig?.dispatcherFunction]);

  const identifyEventRef = useRef<amplitude.Identify>(new amplitude.Identify());

  const updateUserAccounts = async (userData: UserInfoResponse): Promise<void> => {
    const workspaceId = userData?.workspaceMemberships?.[0]?.workspaceId;
    // TODO: Confirm if this is the reason we see the "wrong workspace" error
    // whenever we login as a new user with a new workspace without logging out first
    if (!currentUserWorkspaceId && workspaceId) {
      setCurrentUserWorkspaceId(() => workspaceId);
    }
    await bulkUpsertWorkspace(userData?.workspaces || []);

    if (userData?.workspaceMemberships?.length > 0) {
      await bulkUpsertWorkspaceMembership(userData?.workspaceMemberships || []);
    }

    if (userData?.accounts?.length > 0) {
      await bulkUpsertAccount(userData?.accounts || []);
    }

    await upsertMyAccount(userData);

    const accountRecord = await db.query.account
      .findFirst({
        where: eq(account.mine, true),
      })
      .execute();
    if (!account?.preferredLanguage && accountRecord?.id) {
      await db
        .update(account)
        .set({ preferredLanguage: sessionStorageValue || "none" })
        .where(eq(account.id, accountRecord.id))
        .execute();
    }

    const buildVersion = config.releaseTag;
    identifyEventRef.current.set("buildVersion", buildVersion);
    amplitude.identify(identifyEventRef.current);
    ampli.authSuccess({ "Auth0 App": "SMS" });
    if (accountRecord?.id) {
      const identifyPayload = {
        kind: "user",
        key: accountRecord.id,
        accountId: accountRecord.id,
        name: accountRecord?.name,
        email: accountRecord?.email,
        sbAccount: accountRecord?.email?.includes("@storyboard.fm"),
        workspaceId: workspaceId,
        anonymous: false,
        buildVersion,
      };
      Sentry.setUser({
        id: accountRecord.id,
        email: accountRecord?.email,
        username: accountRecord?.name,
        workspaceId: workspaceId,
        buildVersion,
        sbAccount: accountRecord?.email?.includes("@storyboard.fm"),
      });
      ampli.client.setUserId(accountRecord.id);

      if (ldClient) {
        await identifyLaunchDarklyUser(ldClient, identifyPayload, accountRecord.id);
      } else {
        log.warn("LaunchDarkly client not available for user identification");
      }

      ResetCoordinator.getInstance().updateUserMetadata({
        accountId: accountRecord.id,
        workspaceId,
        workspaceMembershipId: workspaceMembership?.id,
      });
    } else {
      log.warn("No Account ID");
    }
  };

  const setVersionCallback = useCallback(
    (_: any, value: string) => {
      if (!isElectron) return;
      setAttribute("electronVersion", value);

      // Create a new Identify object to avoid property accumulation
      const identifyEvent = new amplitude.Identify();
      identifyEvent.set("electronVersion", value);
      amplitude.identify(identifyEvent);

      setAppVersion(() => value);
    },
    [setAttribute, isElectron],
  );

  useEffect(() => {
    if (user?.accounts?.length > 0) {
      updateUserAccounts(user);
    }
  }, [user]);

  useEffect(() => {
    ipcRendererSend("fetch-version");
    ipcRendererCallback("set-version", setVersionCallback);
    return () => {
      removeIpcListener("set-version", setVersionCallback);
    };
  }, [setVersionCallback]);

  const tokens = user?.devices?.map((device) => device.deviceToken);
  const fetchUserDevice = (token: string): Device | null => {
    return user?.devices?.find((device) => device.deviceToken === token && device.enabled) || null;
  };

  const invalidWorkspace =
    currentUserWorkspaceId &&
    user?.workspaces?.length > 0 &&
    !user?.workspaces?.find((ws) => ws.id === currentUserWorkspaceId);

  const defaultState = (
    <>
      {user?.accounts?.length > 0 && (
        <>
          <ElectronComponentLoader component={<ElectronNotifications tokens={tokens} user={user} />} />
        </>
      )}
      {children}
    </>
  );

  const workspaceIdIsNull = workspaceId === null || workspaceId === "null";

  return (
    <UserContext.Provider
      value={{
        user,
        workspaceId: currentUserWorkspaceId,
        workspaceMembership,
        tokens,
        fetchUserDevice,
        appVersion,
        dispatcherFunction,
        setDispatcherFunction,
      }}
    >
      {invalidWorkspace && !workspaceIdIsNull && (
        <InvalidWorkspaceModal open={true} allowClose={false} workspaceId={workspaceId} />
      )}
      {(!user || (user && !workspaceId)) && (
        <>
          <LoadingScreen />
        </>
      )}
      {((user && !user?.workspaces?.length) || workspaceIdIsNull) && <NoValidWorkspacesModal open={true} />}
      {user && user?.workspaces?.length && defaultState}
    </UserContext.Provider>
  );
};

export default UserProvider;
